import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import api from '../../services/api'
import {
  Layers,
  Users,
  CheckCircle2,
  Clock,
  AlertCircle,
  FileText,
  Download,
  X,
  ChevronRight,
  Search,
  MessageSquare,
  ArrowRight,
  Check,
  Send,
  ExternalLink,
} from 'lucide-react'
import LoadingSpinner from '../../components/LoadingSpinner'
import ProjectStageReviewCard from '../../components/projectlab/ProjectStageReviewCard'

export default function TrainerProjectLab() {
  const { enrollmentId: paramEnrollmentId } = useParams()
  const navigate = useNavigate()

  const [loading, setLoading] = useState(true)
  const [participants, setParticipants] = useState([])
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')

  // Selected participant for review drawer
  const [selectedEnrollmentId, setSelectedEnrollmentId] = useState(paramEnrollmentId || null)
  const [detailLoading, setDetailLoading] = useState(false)
  const [detailData, setDetailData] = useState(null)
  const [reviewFeedback, setReviewFeedback] = useState('')
  const [submittingReview, setSubmittingReview] = useState(false)
  const [reviewMessage, setReviewMessage] = useState(null)
  const [reviewError, setReviewError] = useState(null)

  // Load assigned participants list
  const loadParticipants = async () => {
    try {
      setLoading(true)
      const res = await api.get('/project-lab/trainer/participants')
      setParticipants(res.data?.participants || [])
    } catch (err) {
      console.error('[TrainerProjectLab] Failed to load participants:', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadParticipants()
  }, [])

  // Load participant detail when selected
  useEffect(() => {
    if (!selectedEnrollmentId) {
      setDetailData(null)
      return
    }

    async function loadDetail() {
      try {
        setDetailLoading(true)
        setReviewError(null)
        setReviewMessage(null)
        const res = await api.get(`/project-lab/trainer/projects/${selectedEnrollmentId}`)
        setDetailData(res.data)
      } catch (err) {
        console.error('[TrainerProjectLab] Failed to load participant detail:', err)
        setReviewError(err.response?.data?.error || 'Failed to load project details.')
      } finally {
        setDetailLoading(false)
      }
    }
    loadDetail()
  }, [selectedEnrollmentId])

  // Handle Mentor Review Action ('request_revision' or 'approve')
  const handleReviewAction = async (action) => {
    if (!detailData) return
    const stageIndex = detailData.enrollment.current_stage_index

    if (action === 'request_revision' && (!reviewFeedback || reviewFeedback.trim().length < 10)) {
      setReviewError('Please provide at least 10 characters of constructive critique before requesting revisions.')
      return
    }

    try {
      setSubmittingReview(true)
      setReviewError(null)
      setReviewMessage(null)

      const res = await api.post(
        `/project-lab/trainer/projects/${selectedEnrollmentId}/stages/${stageIndex}/review`,
        {
          action,
          feedback: reviewFeedback,
        }
      )

      setReviewMessage(res.data?.message || 'Review recorded successfully.')
      setReviewFeedback('')

      // Refresh detail and participants list
      const refreshRes = await api.get(`/project-lab/trainer/projects/${selectedEnrollmentId}`)
      setDetailData(refreshRes.data)
      loadParticipants()
    } catch (err) {
      console.error('[TrainerProjectLab] Review action error:', err)
      setReviewError(err.response?.data?.error || 'Failed to submit review.')
    } finally {
      setSubmittingReview(false)
    }
  }

  // Filter participants
  const filteredParticipants = participants.filter((p) => {
    const fullName = `${p.student_first_name} ${p.student_last_name}`.toLowerCase()
    const matchesSearch =
      fullName.includes(searchTerm.toLowerCase()) ||
      p.student_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.project_title?.toLowerCase().includes(searchTerm.toLowerCase())

    const matchesStatus =
      statusFilter === 'all'
        ? true
        : statusFilter === 'submitted'
        ? p.current_stage_status === 'submitted'
        : statusFilter === 'needs_revision'
        ? p.current_stage_status === 'needs_revision'
        : statusFilter === 'completed'
        ? p.enrollment_status === 'completed'
        : true

    return matchesSearch && matchesStatus
  })

  const currentStage = detailData?.stages?.find(
    (s) => s.stage_index === detailData?.enrollment?.current_stage_index
  )

  return (
    <div className="space-y-8 pb-16">
      {/* Header */}
      <div className="border-b border-white/10 pb-6">
        <span className="cinema-label block mb-1">Mentor Workspace</span>
        <h1 className="font-display text-2xl sm:text-3xl text-white font-light">
          Project Lab Reviews
        </h1>
        <p className="text-xs sm:text-sm text-cinema-muted mt-1 max-w-2xl">
          Review participant submissions, provide professional critique, and approve progression through the commercial production lifecycle.
        </p>
      </div>

      {/* Filter and Search Bar */}
      <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
        <div className="relative w-full sm:w-80">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-cinema-muted" />
          <input
            type="text"
            placeholder="Search participant, project, email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-9 pr-4 py-2 bg-white/[0.03] border border-white/10 rounded-none text-xs text-white placeholder-cinema-muted focus:outline-none focus:border-cinema-gold"
          />
        </div>

        <div className="flex items-center gap-2 w-full sm:w-auto">
          {['all', 'submitted', 'needs_revision', 'completed'].map((st) => (
            <button
              key={st}
              onClick={() => setStatusFilter(st)}
              className={`px-3 py-1.5 rounded-none text-xs font-medium capitalize transition-colors ${
                statusFilter === st
                  ? 'bg-cinema-gold text-black font-semibold'
                  : 'bg-white/[0.03] border border-white/10 text-cinema-muted hover:text-white'
              }`}
            >
              {st === 'submitted' ? 'Needs Review' : st.replace('_', ' ')}
            </button>
          ))}
        </div>
      </div>

      {/* ─── STRUCTURED TABLE (Constraint 15: No card-heavy UI) ─────────── */}
      <div className="border border-white/10 rounded-none overflow-hidden bg-white/[0.01]">
        {loading ? (
          <div className="py-12">
            <LoadingSpinner label="Loading assigned participants..." />
          </div>
        ) : filteredParticipants.length === 0 ? (
          <div className="py-16 text-center text-xs text-cinema-muted">
            No participants found matching your criteria.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead className="bg-white/[0.03] border-b border-white/10 text-cinema-muted uppercase tracking-wider font-mono">
                <tr>
                  <th className="px-5 py-3.5 font-semibold">Participant</th>
                  <th className="px-5 py-3.5 font-semibold">Project</th>
                  <th className="px-5 py-3.5 font-semibold">Current Stage</th>
                  <th className="px-5 py-3.5 font-semibold">Review Status</th>
                  <th className="px-5 py-3.5 font-semibold">Latest Activity</th>
                  <th className="px-5 py-3.5 font-semibold text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5 font-sans">
                {filteredParticipants.map((p) => {
                  const isPending = p.current_stage_status === 'submitted'
                  const isRevision = p.current_stage_status === 'needs_revision'
                  const isComplete = p.enrollment_status === 'completed'

                  return (
                    <tr
                      key={p.enrollment_id}
                      className={`hover:bg-white/[0.02] transition-colors ${
                        selectedEnrollmentId === p.enrollment_id ? 'bg-cinema-gold/5' : ''
                      }`}
                    >
                      {/* Participant */}
                      <td className="px-5 py-4">
                        <div className="font-medium text-white text-sm">
                          {p.student_first_name} {p.student_last_name}
                        </div>
                        <div className="text-[11px] text-cinema-muted mt-0.5">{p.student_email}</div>
                      </td>

                      {/* Project */}
                      <td className="px-5 py-4">
                        <div className="text-white font-medium">{p.project_title}</div>
                        <span className="text-[10px] text-cinema-muted capitalize block mt-0.5">
                          {p.mode} mode
                        </span>
                      </td>

                      {/* Current Stage */}
                      <td className="px-5 py-4">
                        <div className="text-white font-medium flex items-center gap-1.5">
                          <span className="font-mono text-cinema-gold">0{p.current_stage_index || 1}</span>
                          <span>{p.current_stage_title || 'Brief'}</span>
                        </div>
                      </td>

                      {/* Status */}
                      <td className="px-5 py-4">
                        <span
                          className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-none text-[11px] font-medium border capitalize ${
                            isPending
                              ? 'bg-cyan-500/10 text-cyan-300 border-cyan-500/30'
                              : isRevision
                              ? 'bg-amber-500/10 text-amber-300 border-amber-500/30'
                              : isComplete
                              ? 'bg-emerald-500/10 text-emerald-300 border-emerald-500/30'
                              : 'bg-white/5 text-cinema-soft border-white/10'
                          }`}
                        >
                          {isPending && <Clock className="w-3 h-3 animate-pulse" />}
                          {isRevision && <AlertCircle className="w-3 h-3" />}
                          {isComplete && <CheckCircle2 className="w-3 h-3" />}
                          {isPending ? 'Awaiting Review' : p.current_stage_status?.replace('_', ' ')}
                        </span>
                      </td>

                      {/* Latest Activity */}
                      <td className="px-5 py-4 text-cinema-muted font-mono text-[11px]">
                        {p.stage_submitted_at
                          ? new Date(p.stage_submitted_at).toLocaleDateString()
                          : new Date(p.enrolled_at).toLocaleDateString()}
                      </td>

                      {/* Actions */}
                      <td className="px-5 py-4 text-right">
                        <button
                          onClick={() => {
                            setSelectedEnrollmentId(p.enrollment_id)
                          }}
                          className={`px-3 py-1.5 rounded-none text-xs font-medium transition-colors ${
                            isPending
                              ? 'bg-cinema-gold text-black font-semibold hover:bg-yellow-400'
                              : 'border border-white/10 text-cinema-soft hover:text-white hover:bg-white/5'
                          }`}
                        >
                          {isPending ? 'Review Work' : 'Inspect Workspace'}
                        </button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ─── REVIEW & INSPECTION DRAWER ─────────────────────────────────── */}
      {selectedEnrollmentId && (
        <div className="fixed inset-0 z-50 overflow-hidden bg-black/70 flex justify-end">
          <div className="w-full max-w-2xl bg-zinc-950 border-l border-white/10 h-full overflow-y-auto p-6 sm:p-8 space-y-6 shadow-2xl">
            
            {/* Drawer Header */}
            <div className="flex items-start justify-between border-b border-white/10 pb-4">
              <div>
                <span className="cinema-label text-[10px] uppercase tracking-wider text-cinema-gold block">
                  Participant Review
                </span>
                <h2 className="font-display text-xl sm:text-2xl text-white font-light">
                  {detailData?.participant?.first_name} {detailData?.participant?.last_name}
                </h2>
                <p className="text-xs text-cinema-muted mt-0.5">
                  {detailData?.project?.title} • {detailData?.participant?.email}
                </p>
              </div>
              <button
                onClick={() => setSelectedEnrollmentId(null)}
                className="p-1.5 rounded-none border border-white/10 text-cinema-muted hover:text-white hover:bg-white/5"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {detailLoading ? (
              <div className="py-16">
                <LoadingSpinner label="Loading participant workspace..." />
              </div>
            ) : detailData ? (
              <div className="space-y-6">

                {/* Alerts */}
                {reviewError && (
                  <div className="bg-red-500/10 border border-red-500/30 rounded-none p-3.5 flex items-start gap-2.5 text-xs text-red-200">
                    <AlertCircle className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
                    <span>{reviewError}</span>
                  </div>
                )}
                {reviewMessage && (
                  <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-none p-3.5 flex items-start gap-2.5 text-xs text-emerald-200">
                    <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
                    <span>{reviewMessage}</span>
                  </div>
                )}

                {/* Stepper overview */}
                <div className="space-y-2">
                  <span className="text-[10px] font-mono uppercase tracking-wider text-cinema-muted block">
                    Lifecycle Timeline
                  </span>
                  <div className="grid gap-1.5" style={{ gridTemplateColumns: `repeat(${(detailData.stages || []).length || 1}, minmax(0,1fr))` }}>
                    {detailData.stages.map((st) => {
                      const isCurr = st.stage_index === detailData.enrollment.current_stage_index
                      const isAppr = st.status === 'approved' || st.status === 'completed'
                      const isSub = st.status === 'submitted'
                      const isRev = st.status === 'needs_revision'

                      return (
                        <div
                          key={st.stage_index}
                          title={`Stage ${st.stage_index}: ${st.title} (${st.status})`}
                          className={`h-2 rounded-none transition-all ${
                            isCurr
                              ? 'bg-cinema-gold ring-2 ring-cinema-gold/30'
                              : isAppr
                              ? 'bg-emerald-500'
                              : isSub
                              ? 'bg-cyan-400 animate-pulse'
                              : isRev
                              ? 'bg-amber-400'
                              : 'bg-white/10'
                          }`}
                        />
                      )
                    })}
                  </div>
                </div>

                {/* Current Stage Card */}
                <div className="bg-white/[0.02] border border-white/10 rounded-none p-5 space-y-4">
                  <div className="flex items-center justify-between border-b border-white/10 pb-3">
                    <div>
                      <span className="text-[10px] uppercase font-mono tracking-wider text-cinema-gold block">
                        Actionable Target
                      </span>
                      <h3 className="text-base text-white font-medium mt-0.5">
                        Stage {currentStage?.stage_index}: {currentStage?.title}
                      </h3>
                    </div>
                    <span
                      className={`text-xs px-2.5 py-0.5 rounded-none border font-medium capitalize ${
                        currentStage?.status === 'submitted'
                          ? 'bg-cyan-500/10 text-cyan-400 border-cyan-500/30'
                          : currentStage?.status === 'needs_revision'
                          ? 'bg-amber-500/10 text-amber-400 border-amber-500/30'
                          : currentStage?.status === 'approved'
                          ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30'
                          : 'bg-white/5 text-cinema-soft border-white/10'
                      }`}
                    >
                      {currentStage?.status?.replace('_', ' ')}
                    </span>
                  </div>

                  <p className="text-xs text-cinema-soft leading-relaxed">
                    {currentStage?.guide?.objective || currentStage?.title}
                  </p>

                  {/* Submission Details */}
                  {currentStage?.latest_submission ? (
                    <div className="space-y-3 pt-2">
                      <div className="bg-black/30 border border-white/5 rounded-none p-3.5 space-y-2">
                        <div className="flex items-center justify-between text-[11px]">
                          <span className="font-mono text-cinema-gold font-semibold">
                            Submission (v{currentStage.latest_submission.version})
                          </span>
                          <span className="text-cinema-muted font-mono">
                            {new Date(currentStage.latest_submission.created_at).toLocaleString()}
                          </span>
                        </div>

                        {currentStage.latest_submission.participant_notes && (
                          <p className="text-xs text-cinema-soft italic border-l-2 border-white/15 pl-2.5 py-0.5">
                            "{currentStage.latest_submission.participant_notes}"
                          </p>
                        )}

                        {/* Files to download */}
                        {currentStage.latest_submission.files?.length > 0 && (
                          <div className="space-y-1.5 pt-2">
                            <span className="text-[10px] font-mono uppercase tracking-wider text-cinema-muted block">
                              Attached Work Files ({currentStage.latest_submission.files.length})
                            </span>
                            <div className="divide-y divide-white/5">
                              {currentStage.latest_submission.files.map((file) => (
                                <div
                                  key={file.id}
                                  className="py-1.5 flex items-center justify-between text-xs"
                                >
                                  <div className="flex items-center gap-2 truncate max-w-[260px]">
                                    <FileText className="w-3.5 h-3.5 text-cinema-gold shrink-0" />
                                    <span className="font-mono text-white truncate">{file.original_filename}</span>
                                  </div>
                                  <a
                                    href={`/api/project-lab/files/${file.id}/download`}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="px-2.5 py-1 bg-white/5 hover:bg-white/10 text-cinema-gold rounded-none text-[11px] font-medium transition-colors flex items-center gap-1 shrink-0"
                                  >
                                    <Download className="w-3 h-3" /> Download
                                  </a>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : currentStage?.submission_notes ? (
                    <div className="bg-black/30 border border-white/5 rounded-none p-3 text-xs text-cinema-soft">
                      <span className="font-mono text-[10px] uppercase text-cinema-muted block mb-1">
                        Participant Confirmation Notes
                      </span>
                      "{currentStage.submission_notes}"
                    </div>
                  ) : (
                    <p className="text-xs text-cinema-muted italic">
                      Participant is currently working through this stage. No work submitted yet.
                    </p>
                  )}
                </div>

                {/* AI-013 Project Lab Milestone Review & Delivery Assistant */}
                <ProjectStageReviewCard
                  enrollmentId={selectedEnrollmentId}
                  stageIndex={detailData.enrollment.current_stage_index}
                  apiPrefix="/project-lab/trainer/projects"
                  onApplyDraft={(draft) => setReviewFeedback(draft)}
                />

                {/* ─── MENTOR REVIEW ACTIONS FORM ─────────────────────────── */}
                <div className="bg-white/[0.02] border border-white/10 rounded-none p-5 space-y-4">
                  <span className="text-[10px] uppercase font-mono tracking-wider text-white block">
                    Mentor Feedback & Assessment
                  </span>

                  <textarea
                    rows={4}
                    placeholder="Provide constructive, professional critique and actionable guidance for the participant..."
                    value={reviewFeedback}
                    onChange={(e) => setReviewFeedback(e.target.value)}
                    className="w-full bg-black/40 border border-white/10 rounded-none p-3 text-xs text-white placeholder-cinema-muted focus:outline-none focus:border-cinema-gold"
                  />
                  <div className="flex items-center justify-between text-[11px] text-cinema-muted">
                    <span>Constructive feedback required for revision requests.</span>
                    <span className={reviewFeedback.length < 10 ? 'text-amber-400' : 'text-emerald-400'}>
                      {reviewFeedback.length}/10 chars min
                    </span>
                  </div>

                  <div className="flex items-center justify-end gap-3 pt-2">
                    <button
                      onClick={() => handleReviewAction('request_revision')}
                      disabled={submittingReview || reviewFeedback.trim().length < 10}
                      className="px-4 py-2.5 bg-amber-500/20 border border-amber-500/40 text-amber-300 hover:bg-amber-500/30 rounded-none text-xs font-semibold transition-colors disabled:opacity-50"
                    >
                      Request Revision
                    </button>
                    <button
                      onClick={() => handleReviewAction('approve')}
                      disabled={submittingReview}
                      className="px-5 py-2.5 bg-cinema-gold text-black hover:bg-yellow-400 rounded-none text-xs font-bold transition-colors disabled:opacity-50 flex items-center gap-1.5"
                    >
                      <Check className="w-4 h-4" />
                      {currentStage?.stage_index === (detailData.stages || []).length ? 'Approve & Mark Complete' : 'Approve Stage'}
                    </button>
                  </div>
                </div>

                {/* Full Audit Trail of Past Submissions (Constraint 2) */}
                <div className="space-y-3 pt-2">
                  <span className="text-[10px] font-mono uppercase tracking-wider text-cinema-muted block">
                    All Historical Submissions ({detailData.submissions.length})
                  </span>
                  {detailData.submissions.length === 0 ? (
                    <p className="text-xs text-cinema-muted italic">No prior submissions.</p>
                  ) : (
                    <div className="space-y-2.5">
                      {detailData.submissions.map((s) => (
                        <div key={s.id} className="bg-black/20 border border-white/5 rounded-none p-3 space-y-1.5">
                          <div className="flex items-center justify-between text-[11px]">
                            <span className="font-medium text-white">
                              Stage {s.stage_index}: {s.stage_name} (v{s.version})
                            </span>
                            <span
                              className={`text-[10px] px-2 py-0.2 rounded-none border capitalize ${
                                s.status === 'approved'
                                  ? 'text-emerald-400 border-emerald-500/30'
                                  : s.status === 'needs_changes'
                                  ? 'text-amber-400 border-amber-500/30'
                                  : 'text-cyan-400 border-cyan-500/30'
                              }`}
                            >
                              {s.status?.replace('_', ' ')}
                            </span>
                          </div>
                          {s.feedback && (
                            <p className="text-[11px] text-cinema-muted border-l border-white/10 pl-2">
                              Mentor Feedback: "{s.feedback}"
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

              </div>
            ) : null}

          </div>
        </div>
      )}

    </div>
  )
}
